/**
 * TreeViewPropertyEditor
 *
 * Property editor panel for the 'tree-view' control type.
 * Covers all six configuration sections:
 *   1. Data Source      — static JSON textarea + binding hint
 *   2. Field Mapping    — id, label, description, children, icon fields
 *   3. Appearance       — showLines, showIcons, indentSize, maxHeight
 *   4. Interaction      — collapsible, defaultExpanded, selectable, multiSelect
 *   5. Node Actions     — action cards (url / form / event) with add/remove
 *   6. Dynamic Link     — per-node URL/label/openIn from node data fields
 *
 * SRP: responsible ONLY for rendering the tree-view property panel.
 * Export: TreeViewPropertyEditor component + TREE_VIEW_PROPERTY_EDITOR_TYPE constant
 *         + a PropertyEditor registration object (TreeViewEditorRegistration)
 *         for use in loadBuiltInEditors.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormControl } from '@atlas-forms/types-js';
import type { PropertyEditor, PropertyEditorProps } from '../../registry/PropertyEditor.types';
import FormLookupSelect from '../FormContainers/FormLookupSelect';

// ─── Config Types ─────────────────────────────────────────────────────────────

interface NodeAction {
  id: string;
  label: string;
  actionType: 'url' | 'form' | 'event';
  urlTemplate?: string;
  openIn?: 'same-tab' | 'new-tab';
  formId?: string;
  formIdField?: string;
  eventName?: string;
}

interface TreeViewConfig {
  data?: unknown;
  idField?: string;
  labelField?: string;
  descriptionField?: string;
  childrenField?: string;
  iconField?: string;
  showLines?: boolean;
  showIcons?: boolean;
  indentSize?: number;
  maxHeight?: string;
  defaultExpanded?: boolean | number;
  collapsible?: boolean;
  selectable?: boolean;
  multiSelect?: boolean;
  nodeActions?: NodeAction[];
  nodeLinkUrlField?: string;
  nodeLinkLabelField?: string;
  nodeLinkOpenIn?: 'same-tab' | 'new-tab';
}

// ─── Public Props ─────────────────────────────────────────────────────────────

export interface TreeViewPropertyEditorProps {
  control: FormControl;
  onUpdateControl: (patch: Partial<FormControl>) => void;
  isDark: boolean;
}

// ─── Shared Styles ────────────────────────────────────────────────────────────

function makeStyles(isDark: boolean) {
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '5px 8px',
    borderRadius: 5,
    border: `1px solid ${isDark ? 'rgba(71,85,105,0.6)' : '#d1d5db'}`,
    background: isDark ? 'rgba(15,23,42,0.7)' : '#fff',
    color: isDark ? '#e2e8f0' : '#1e293b',
    fontSize: 12,
    boxSizing: 'border-box' as const,
    outline: 'none',
    fontFamily: 'inherit',
  };

  const monoInputStyle: React.CSSProperties = {
    ...inputStyle,
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
    resize: 'vertical' as const,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: isDark ? '#94a3b8' : '#64748b',
    marginBottom: 4,
    display: 'block',
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: 20,
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: isDark ? '#64748b' : '#9ca3af',
    marginBottom: 10,
    paddingBottom: 6,
    borderBottom: `1px solid ${isDark ? 'rgba(51,65,85,0.4)' : '#f1f5f9'}`,
    margin: '0 0 10px 0',
  };

  const infoTextStyle: React.CSSProperties = {
    fontSize: 11,
    color: isDark ? '#64748b' : '#94a3b8',
    marginTop: 6,
    lineHeight: 1.5,
  };

  const errorTextStyle: React.CSSProperties = {
    fontSize: 11,
    color: '#ef4444',
    marginTop: 4,
  };

  const toggleRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  };

  const toggleLabelStyle: React.CSSProperties = {
    fontSize: 12,
    color: isDark ? '#e2e8f0' : '#1e293b',
    cursor: 'pointer',
    userSelect: 'none' as const,
  };

  const twoColGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
  };

  const fieldGroupStyle: React.CSSProperties = {
    marginBottom: 8,
  };

  const actionCardStyle: React.CSSProperties = {
    background: isDark ? 'rgba(15,23,42,0.6)' : '#f8fafc',
    border: `1px solid ${isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0'}`,
    borderRadius: 6,
    padding: '10px 12px',
    marginBottom: 8,
    position: 'relative' as const,
  };

  const cardHeaderRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  };

  const removeBtnStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: isDark ? '#94a3b8' : '#64748b',
    cursor: 'pointer',
    fontSize: 16,
    lineHeight: 1,
    padding: '2px 4px',
    borderRadius: 4,
    flexShrink: 0,
  };

  const addBtnStyle: React.CSSProperties = {
    background: 'transparent',
    border: `1px solid ${isDark ? '#22c55e' : '#16a34a'}`,
    color: isDark ? '#22c55e' : '#16a34a',
    borderRadius: 5,
    padding: '4px 10px',
    fontSize: 12,
    cursor: 'pointer',
    fontWeight: 600,
  };

  const sectionHeaderRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
    paddingBottom: 6,
    borderBottom: `1px solid ${isDark ? 'rgba(51,65,85,0.4)' : '#f1f5f9'}`,
  };

  const sectionHeaderTitleStyle: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: isDark ? '#64748b' : '#9ca3af',
    margin: 0,
  };

  return {
    inputStyle,
    monoInputStyle,
    labelStyle,
    sectionStyle,
    sectionTitleStyle,
    infoTextStyle,
    errorTextStyle,
    toggleRowStyle,
    toggleLabelStyle,
    twoColGridStyle,
    fieldGroupStyle,
    actionCardStyle,
    cardHeaderRowStyle,
    removeBtnStyle,
    addBtnStyle,
    sectionHeaderRowStyle,
    sectionHeaderTitleStyle,
  };
}

// ─── Helper ───────────────────────────────────────────────────────────────────

function updateConfig(
  control: FormControl,
  patch: Partial<TreeViewConfig>,
  onUpdateControl: (patch: Partial<FormControl>) => void,
): void {
  onUpdateControl({ config: { ...(control.config ?? {}), ...patch } });
}

// ─── Section 1: Data Source ───────────────────────────────────────────────────

interface DataSourceSectionProps {
  control: FormControl;
  onUpdateControl: (patch: Partial<FormControl>) => void;
  isDark: boolean;
  styles: ReturnType<typeof makeStyles>;
}

function DataSourceSection({ control, onUpdateControl, styles }: DataSourceSectionProps) {
  const cfg = (control.config ?? {}) as TreeViewConfig;
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [localValue, setLocalValue] = useState<string>(() => {
    try {
      return cfg.data !== undefined ? JSON.stringify(cfg.data, null, 2) : '';
    } catch {
      return '';
    }
  });

  // Guard: don't overwrite active user input when config.data changes externally.
  const focusedRef = useRef(false);

  // Sync textarea when config.data changes externally (e.g. undo/redo).
  const configData = (control.config as TreeViewConfig | undefined)?.data;
  useEffect(() => {
    if (focusedRef.current) return;
    try {
      setLocalValue(configData !== undefined ? JSON.stringify(configData, null, 2) : '');
    } catch {
      // keep current value if serialization fails
    }
  }, [configData]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalValue(e.target.value);
    setJsonError(null);
  }, []);

  const handleBlur = useCallback(() => {
    focusedRef.current = false;
    const raw = localValue.trim();
    if (!raw) {
      updateConfig(control, { data: undefined }, onUpdateControl);
      setJsonError(null);
      return;
    }
    try {
      const parsed: unknown = JSON.parse(raw);
      updateConfig(control, { data: parsed }, onUpdateControl);
      setJsonError(null);
    } catch {
      setJsonError('Invalid JSON');
    }
  }, [localValue, control, onUpdateControl]);

  return (
    <div style={styles.sectionStyle}>
      <p style={styles.sectionTitleStyle}>Data Source</p>
      <div style={styles.fieldGroupStyle}>
        <label htmlFor="tvpe-static-data" style={styles.labelStyle}>
          Static Data (JSON)
        </label>
        <textarea
          id="tvpe-static-data"
          rows={5}
          value={localValue}
          onChange={handleChange}
          onFocus={() => { focusedRef.current = true; }}
          onBlur={handleBlur}
          style={styles.monoInputStyle}
          aria-label="Static tree data JSON"
          placeholder='[{"id":1,"name":"Root","children":[]}]'
          spellCheck={false}
        />
        {jsonError && <p style={styles.errorTextStyle}>{jsonError}</p>}
        <p style={styles.infoTextStyle}>
          Or use data binding — set a binding path in the Binding tab to connect live data.
        </p>
      </div>
    </div>
  );
}

// ─── Section 2: Field Mapping ─────────────────────────────────────────────────

interface FieldMappingSectionProps {
  control: FormControl;
  onUpdateControl: (patch: Partial<FormControl>) => void;
  styles: ReturnType<typeof makeStyles>;
}

interface FieldDef {
  key: keyof TreeViewConfig;
  label: string;
  placeholder: string;
  ariaLabel: string;
}

const FIELD_DEFS: FieldDef[] = [
  { key: 'idField', label: 'ID Field', placeholder: 'id', ariaLabel: 'ID field name' },
  { key: 'labelField', label: 'Label Field', placeholder: 'name', ariaLabel: 'Label field name' },
  { key: 'descriptionField', label: 'Description Field', placeholder: 'description', ariaLabel: 'Description field name' },
  { key: 'childrenField', label: 'Children Field', placeholder: 'children', ariaLabel: 'Children array field name' },
  { key: 'iconField', label: 'Icon Field', placeholder: 'icon — optional', ariaLabel: 'Icon field name (optional)' },
];

function FieldMappingSection({ control, onUpdateControl, styles }: FieldMappingSectionProps) {
  const cfg = (control.config ?? {}) as TreeViewConfig;

  const handleChange = useCallback(
    (key: keyof TreeViewConfig, value: string) => {
      updateConfig(control, { [key]: value || undefined } as Partial<TreeViewConfig>, onUpdateControl);
    },
    [control, onUpdateControl],
  );

  return (
    <div style={styles.sectionStyle}>
      <p style={styles.sectionTitleStyle}>Field Mapping</p>
      <div style={styles.twoColGridStyle}>
        {FIELD_DEFS.map((fd) => (
          <div key={fd.key} style={styles.fieldGroupStyle}>
            <label htmlFor={`tvpe-${fd.key}`} style={styles.labelStyle}>
              {fd.label}
            </label>
            <input
              id={`tvpe-${fd.key}`}
              type="text"
              value={(cfg[fd.key] as string | undefined) ?? ''}
              onChange={(e) => handleChange(fd.key, e.target.value)}
              placeholder={fd.placeholder}
              style={styles.inputStyle}
              aria-label={fd.ariaLabel}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Section 3: Appearance ────────────────────────────────────────────────────

interface AppearanceSectionProps {
  control: FormControl;
  onUpdateControl: (patch: Partial<FormControl>) => void;
  styles: ReturnType<typeof makeStyles>;
}

function AppearanceSection({ control, onUpdateControl, styles }: AppearanceSectionProps) {
  const cfg = (control.config ?? {}) as TreeViewConfig;

  const handleToggle = useCallback(
    (key: keyof TreeViewConfig, value: boolean) => {
      updateConfig(control, { [key]: value } as Partial<TreeViewConfig>, onUpdateControl);
    },
    [control, onUpdateControl],
  );

  const handleIndentChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = parseInt(e.target.value, 10);
      if (!isNaN(val)) {
        updateConfig(control, { indentSize: Math.min(48, Math.max(8, val)) }, onUpdateControl);
      }
    },
    [control, onUpdateControl],
  );

  const handleMaxHeightChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value.trim();
      updateConfig(control, { maxHeight: val || undefined }, onUpdateControl);
    },
    [control, onUpdateControl],
  );

  return (
    <div style={styles.sectionStyle}>
      <p style={styles.sectionTitleStyle}>Appearance</p>

      <label style={styles.toggleRowStyle}>
        <input
          type="checkbox"
          checked={cfg.showLines ?? true}
          onChange={(e) => handleToggle('showLines', e.target.checked)}
          aria-label="Show connector lines"
        />
        <span style={styles.toggleLabelStyle}>Show connector lines</span>
      </label>

      <label style={styles.toggleRowStyle}>
        <input
          type="checkbox"
          checked={cfg.showIcons ?? true}
          onChange={(e) => handleToggle('showIcons', e.target.checked)}
          aria-label="Show folder/file icons"
        />
        <span style={styles.toggleLabelStyle}>Show folder/file icons</span>
      </label>

      <div style={styles.twoColGridStyle}>
        <div style={styles.fieldGroupStyle}>
          <label htmlFor="tvpe-indent-size" style={styles.labelStyle}>
            Indent Size (px)
          </label>
          <input
            id="tvpe-indent-size"
            type="number"
            value={cfg.indentSize ?? 20}
            onChange={handleIndentChange}
            min={8}
            max={48}
            style={styles.inputStyle}
            aria-label="Indent size in pixels"
          />
        </div>
        <div style={styles.fieldGroupStyle}>
          <label htmlFor="tvpe-max-height" style={styles.labelStyle}>
            Max Height
          </label>
          <input
            id="tvpe-max-height"
            type="text"
            value={cfg.maxHeight ?? ''}
            onChange={handleMaxHeightChange}
            placeholder="400px, 60vh, auto"
            style={styles.inputStyle}
            aria-label="Maximum height of tree view"
          />
        </div>
      </div>
    </div>
  );
}

// ─── Section 4: Interaction ───────────────────────────────────────────────────

interface InteractionSectionProps {
  control: FormControl;
  onUpdateControl: (patch: Partial<FormControl>) => void;
  styles: ReturnType<typeof makeStyles>;
}

const DEFAULT_EXPANDED_OPTIONS = [
  { value: 'false', label: 'None' },
  { value: 'true', label: 'All' },
  { value: '1', label: '1 level' },
  { value: '2', label: '2 levels' },
  { value: '3', label: '3 levels' },
] as const;

function parseDefaultExpanded(raw: boolean | number | undefined): string {
  if (raw === true) return 'true';
  if (raw === false || raw === undefined) return 'false';
  return String(raw);
}

function InteractionSection({ control, onUpdateControl, styles }: InteractionSectionProps) {
  const cfg = (control.config ?? {}) as TreeViewConfig;

  const handleToggle = useCallback(
    (key: keyof TreeViewConfig, value: boolean) => {
      updateConfig(control, { [key]: value } as Partial<TreeViewConfig>, onUpdateControl);
    },
    [control, onUpdateControl],
  );

  const handleDefaultExpandedChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const val = e.target.value;
      let parsed: boolean | number;
      if (val === 'true') parsed = true;
      else if (val === 'false') parsed = false;
      else parsed = parseInt(val, 10);
      updateConfig(control, { defaultExpanded: parsed }, onUpdateControl);
    },
    [control, onUpdateControl],
  );

  const isSelectable = cfg.selectable ?? false;

  return (
    <div style={styles.sectionStyle}>
      <p style={styles.sectionTitleStyle}>Interaction</p>

      <label style={styles.toggleRowStyle}>
        <input
          type="checkbox"
          checked={cfg.collapsible ?? true}
          onChange={(e) => handleToggle('collapsible', e.target.checked)}
          aria-label="Collapsible nodes"
        />
        <span style={styles.toggleLabelStyle}>Collapsible</span>
      </label>

      <div style={styles.fieldGroupStyle}>
        <label htmlFor="tvpe-default-expanded" style={styles.labelStyle}>
          Default Expanded
        </label>
        <select
          id="tvpe-default-expanded"
          value={parseDefaultExpanded(cfg.defaultExpanded)}
          onChange={handleDefaultExpandedChange}
          style={styles.inputStyle}
          aria-label="Default expanded depth"
        >
          {DEFAULT_EXPANDED_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <label style={styles.toggleRowStyle}>
        <input
          type="checkbox"
          checked={isSelectable}
          onChange={(e) => handleToggle('selectable', e.target.checked)}
          aria-label="Enable node selection"
        />
        <span style={styles.toggleLabelStyle}>Selectable</span>
      </label>

      <label
        style={{
          ...styles.toggleRowStyle,
          opacity: isSelectable ? 1 : 0.45,
          pointerEvents: isSelectable ? 'auto' : 'none',
        }}
      >
        <input
          type="checkbox"
          checked={cfg.multiSelect ?? false}
          onChange={(e) => handleToggle('multiSelect', e.target.checked)}
          disabled={!isSelectable}
          aria-label="Enable multi-select (requires selectable)"
        />
        <span style={styles.toggleLabelStyle}>Multi-select</span>
      </label>
    </div>
  );
}

// ─── Section 5: Node Actions ──────────────────────────────────────────────────

interface NodeActionsSectionProps {
  control: FormControl;
  onUpdateControl: (patch: Partial<FormControl>) => void;
  styles: ReturnType<typeof makeStyles>;
}

function NodeActionsSection({ control, onUpdateControl, styles }: NodeActionsSectionProps) {
  const cfg = (control.config ?? {}) as TreeViewConfig;
  const actions = cfg.nodeActions ?? [];

  const setActions = useCallback(
    (next: NodeAction[]) => {
      updateConfig(control, { nodeActions: next }, onUpdateControl);
    },
    [control, onUpdateControl],
  );

  const handleAdd = useCallback(() => {
    const newAction: NodeAction = {
      id: Date.now().toString(),
      label: 'Action',
      actionType: 'url',
    };
    setActions([...actions, newAction]);
  }, [actions, setActions]);

  const handleRemove = useCallback(
    (id: string) => {
      setActions(actions.filter((a) => a.id !== id));
    },
    [actions, setActions],
  );

  const handleActionChange = useCallback(
    (id: string, patch: Partial<NodeAction>) => {
      setActions(
        actions.map((a) => (a.id === id ? { ...a, ...patch } : a)),
      );
    },
    [actions, setActions],
  );

  return (
    <div style={styles.sectionStyle}>
      <div style={styles.sectionHeaderRowStyle}>
        <p style={styles.sectionHeaderTitleStyle}>Node Actions</p>
        <button type="button" onClick={handleAdd} style={styles.addBtnStyle} aria-label="Add node action">
          ＋ Add Action
        </button>
      </div>

      {actions.length === 0 && (
        <p style={{ ...styles.infoTextStyle, fontStyle: 'italic' }}>
          No actions yet. Add one to attach buttons to each tree node.
        </p>
      )}

      {actions.map((action) => (
        <NodeActionCard
          key={action.id}
          action={action}
          onChange={(patch) => handleActionChange(action.id, patch)}
          onRemove={() => handleRemove(action.id)}
          styles={styles}
        />
      ))}
    </div>
  );
}

interface NodeActionCardProps {
  action: NodeAction;
  onChange: (patch: Partial<NodeAction>) => void;
  onRemove: () => void;
  styles: ReturnType<typeof makeStyles>;
}

function NodeActionCard({ action, onChange, onRemove, styles }: NodeActionCardProps) {
  const openInOptions = useMemo(
    () => [
      { value: 'same-tab', label: 'Same Tab' },
      { value: 'new-tab', label: 'New Tab' },
    ],
    [],
  );

  return (
    <div style={styles.actionCardStyle} role="group" aria-label={`Action: ${action.label}`}>
      <div style={styles.cardHeaderRowStyle}>
        <div style={{ flex: 1, marginRight: 8 }}>
          <label
            htmlFor={`tvpe-action-label-${action.id}`}
            style={styles.labelStyle}
          >
            Label
          </label>
          <input
            id={`tvpe-action-label-${action.id}`}
            type="text"
            value={action.label}
            onChange={(e) => onChange({ label: e.target.value })}
            style={styles.inputStyle}
            aria-label="Action label"
          />
        </div>
        <button
          type="button"
          onClick={onRemove}
          style={{ ...styles.removeBtnStyle, alignSelf: 'flex-end', marginBottom: 2 }}
          aria-label={`Remove action ${action.label}`}
          title="Remove action"
        >
          ×
        </button>
      </div>

      <div style={styles.fieldGroupStyle}>
        <label htmlFor={`tvpe-action-type-${action.id}`} style={styles.labelStyle}>
          Action Type
        </label>
        <select
          id={`tvpe-action-type-${action.id}`}
          value={action.actionType}
          onChange={(e) =>
            onChange({ actionType: e.target.value as NodeAction['actionType'] })
          }
          style={styles.inputStyle}
          aria-label="Action type"
        >
          <option value="url">URL</option>
          <option value="form">Open Form</option>
          <option value="event">Fire Event</option>
        </select>
      </div>

      {action.actionType === 'url' && (
        <>
          <div style={styles.fieldGroupStyle}>
            <label htmlFor={`tvpe-action-url-${action.id}`} style={styles.labelStyle}>
              URL Template
            </label>
            <input
              id={`tvpe-action-url-${action.id}`}
              type="text"
              value={action.urlTemplate ?? ''}
              onChange={(e) => onChange({ urlTemplate: e.target.value || undefined })}
              placeholder="/page/{{id}}"
              style={styles.inputStyle}
              aria-label="URL template (use {{fieldName}} for interpolation)"
            />
          </div>
          <div style={styles.fieldGroupStyle}>
            <label htmlFor={`tvpe-action-open-in-${action.id}`} style={styles.labelStyle}>
              Open In
            </label>
            <select
              id={`tvpe-action-open-in-${action.id}`}
              value={action.openIn ?? 'new-tab'}
              onChange={(e) =>
                onChange({ openIn: e.target.value as NodeAction['openIn'] })
              }
              style={styles.inputStyle}
              aria-label="Open in same tab or new tab"
            >
              {openInOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </>
      )}

      {action.actionType === 'form' && (
        <>
          <FormLookupSelect
            label="Form ID"
            value={action.formId ?? null}
            onChange={(v) => onChange({ formId: v !== null ? String(v) : undefined })}
            placeholder="Select a form..."
          />
          <div style={styles.fieldGroupStyle}>
            <label htmlFor={`tvpe-action-formidfield-${action.id}`} style={styles.labelStyle}>
              Or read from field
            </label>
            <input
              id={`tvpe-action-formidfield-${action.id}`}
              type="text"
              value={action.formIdField ?? ''}
              onChange={(e) => onChange({ formIdField: e.target.value || undefined })}
              placeholder="e.g. formId"
              style={styles.inputStyle}
              aria-label="Node data field containing the form ID"
            />
          </div>
        </>
      )}

      {action.actionType === 'event' && (
        <div style={styles.fieldGroupStyle}>
          <label htmlFor={`tvpe-action-event-${action.id}`} style={styles.labelStyle}>
            Event Name
          </label>
          <input
            id={`tvpe-action-event-${action.id}`}
            type="text"
            value={action.eventName ?? ''}
            onChange={(e) => onChange({ eventName: e.target.value || undefined })}
            placeholder="my-custom-event"
            style={styles.inputStyle}
            aria-label="Custom event name to fire"
          />
        </div>
      )}
    </div>
  );
}

// ─── Section 6: Dynamic Link ──────────────────────────────────────────────────

interface DynamicLinkSectionProps {
  control: FormControl;
  onUpdateControl: (patch: Partial<FormControl>) => void;
  styles: ReturnType<typeof makeStyles>;
}

function DynamicLinkSection({ control, onUpdateControl, styles }: DynamicLinkSectionProps) {
  const cfg = (control.config ?? {}) as TreeViewConfig;

  const handleChange = useCallback(
    (key: keyof TreeViewConfig, value: string) => {
      updateConfig(control, { [key]: value || undefined } as Partial<TreeViewConfig>, onUpdateControl);
    },
    [control, onUpdateControl],
  );

  const handleOpenInChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      updateConfig(
        control,
        { nodeLinkOpenIn: e.target.value as 'same-tab' | 'new-tab' },
        onUpdateControl,
      );
    },
    [control, onUpdateControl],
  );

  return (
    <div style={styles.sectionStyle}>
      <p style={styles.sectionTitleStyle}>Dynamic Link (per node)</p>
      <p style={styles.infoTextStyle}>
        Read link URL directly from a field in each node's data.
      </p>

      <div style={{ marginTop: 8, ...styles.fieldGroupStyle }}>
        <label htmlFor="tvpe-link-url-field" style={styles.labelStyle}>
          URL Field
        </label>
        <input
          id="tvpe-link-url-field"
          type="text"
          value={cfg.nodeLinkUrlField ?? ''}
          onChange={(e) => handleChange('nodeLinkUrlField', e.target.value)}
          placeholder="linkUrl"
          style={styles.inputStyle}
          aria-label="Node data field containing the link URL"
        />
      </div>

      <div style={styles.fieldGroupStyle}>
        <label htmlFor="tvpe-link-label-field" style={styles.labelStyle}>
          Button Label Field
        </label>
        <input
          id="tvpe-link-label-field"
          type="text"
          value={cfg.nodeLinkLabelField ?? ''}
          onChange={(e) => handleChange('nodeLinkLabelField', e.target.value)}
          placeholder="linkLabel"
          style={styles.inputStyle}
          aria-label="Node data field containing the button label"
        />
      </div>

      <div style={styles.fieldGroupStyle}>
        <label htmlFor="tvpe-link-open-in" style={styles.labelStyle}>
          Open In
        </label>
        <select
          id="tvpe-link-open-in"
          value={cfg.nodeLinkOpenIn ?? 'new-tab'}
          onChange={handleOpenInChange}
          style={styles.inputStyle}
          aria-label="Open dynamic link in same tab or new tab"
        >
          <option value="same-tab">Same Tab</option>
          <option value="new-tab">New Tab</option>
        </select>
      </div>
    </div>
  );
}

// ─── Root Component ───────────────────────────────────────────────────────────

/**
 * TreeViewPropertyEditor
 *
 * Standalone property panel for the tree-view control.
 * Can be used directly (with its own props) or via the PropertyEditorRegistry.
 */
export const TreeViewPropertyEditor: React.FC<TreeViewPropertyEditorProps> = ({
  control,
  onUpdateControl,
  isDark,
}) => {
  const styles = useMemo(() => makeStyles(isDark), [isDark]);

  return (
    <div
      style={{
        padding: '12px 14px',
        background: isDark ? 'rgba(15,23,42,0.6)' : '#f8fafc',
        color: isDark ? '#e2e8f0' : '#1e293b',
      }}
    >
      <DataSourceSection
        control={control}
        onUpdateControl={onUpdateControl}
        isDark={isDark}
        styles={styles}
      />
      <FieldMappingSection
        control={control}
        onUpdateControl={onUpdateControl}
        styles={styles}
      />
      <AppearanceSection
        control={control}
        onUpdateControl={onUpdateControl}
        styles={styles}
      />
      <InteractionSection
        control={control}
        onUpdateControl={onUpdateControl}
        styles={styles}
      />
      <NodeActionsSection
        control={control}
        onUpdateControl={onUpdateControl}
        styles={styles}
      />
      <DynamicLinkSection
        control={control}
        onUpdateControl={onUpdateControl}
        styles={styles}
      />
    </div>
  );
};

TreeViewPropertyEditor.displayName = 'TreeViewPropertyEditor';

// ─── Registry Adapter ─────────────────────────────────────────────────────────

/**
 * Thin adapter so the component can be registered in PropertyEditorRegistry.
 * The registry passes { control, onConfigChange, onControlChange, ... };
 * we forward via onControlChange which matches onUpdateControl's signature.
 */
function renderForRegistry(props: PropertyEditorProps): React.ReactNode {
  const { control, onControlChange } = props;

  // Derive isDark from document class (same technique as PropertiesTab via useUIStore,
  // but here we keep the component free of store dependencies).
  const isDark =
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark');

  return (
    <TreeViewPropertyEditor
      control={control}
      onUpdateControl={(patch) => onControlChange(patch)}
      isDark={isDark}
    />
  );
}

/**
 * PropertyEditor registration object — pass to PropertyEditorRegistry.register()
 * or include in the BUILTIN_EDITORS array in loadBuiltInEditors.ts.
 */
export const TreeViewEditorRegistration: PropertyEditor = {
  type: 'tree-view',
  label: 'Tree View',
  description: 'Configure tree data source, field mapping, appearance, interaction, node actions, and dynamic links',
  version: '1.0.0',
  render: renderForRegistry,
};

// ─── Exports ──────────────────────────────────────────────────────────────────

export const TREE_VIEW_PROPERTY_EDITOR_TYPE = 'tree-view' as const;

export default TreeViewPropertyEditor;
